import React, { useState, useEffect, useCallback } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Loader2, AlertTriangle, CheckCircle2, Filter, RefreshCw } from 'lucide-react';

/**
 * CpscBrokerScope — CHA-271. The Settings scope manager for the production-scope gate (operator-ratified
 * 2026-07-12): CPSC broker scope = pod_PrdTyp on the include-list AND shp2 on the US-warehouse allowlist.
 * Lists EVERY distinct value in the live Open WIP feed with invoice counts — PLUS config values absent from
 * today's feed (count 0), so a Save never silently drops a seeded-but-quiet warehouse from the include list.
 * Save writes the include-lists AND the reviewed vocabulary through the audited manageCpscEmailRecipients
 * writer; a NEW ERP value later shows ⚠ here and is loudly flagged in every run's log (an allowlist must
 * never silently shrink CPSC scope). Rows with a blank axis are INCONCLUSIVE and kept in scope fail-safe.
 */
const s = (v) => String(v ?? '').trim();

// Module-level (never re-created per render — self-review F6): a toggle-chip group for one value axis.
function ScopeGroup({ title, rows, kind, onToggle }) {
  return (
    <div className="space-y-1">
      <div className="font-plex-mono text-[10px] uppercase tracking-[0.1em] font-semibold text-carbon-gray-70">{title}</div>
      <div className="flex flex-wrap gap-1.5">
        {rows.map((r) => (
          <button key={r.value} onClick={() => onToggle(kind, r.value)} disabled={r.value === '?'}
            aria-label={`scope ${kind} ${r.value}`}
            title={r.value === '?' ? 'Blank in the data — rows with a blank axis are INCONCLUSIVE and kept in scope fail-safe (surfaced per run)' : (r.reviewed ? '' : 'NEW value — never reviewed')}
            aria-pressed={r.included}
            className={`px-2 py-1 border font-plex-mono text-[11px] transition-colors ${
              r.value === '?' ? 'border-carbon-border text-carbon-gray-40 cursor-not-allowed'
                : r.included ? 'bg-carbon-ink text-carbon-white border-carbon-ink'
                  : 'bg-carbon-white text-carbon-gray-70 border-carbon-gray-30 hover:bg-carbon-gray-10'
            }`}>
            {r.value} <span className={r.included ? 'text-white/70' : 'text-carbon-gray-40'}>({r.invoices})</span>
            {!r.reviewed && r.value !== '?' && <span className="ml-1 text-carbon-orange-70 font-semibold">⚠ new</span>}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function CpscBrokerScope({ arg }) {
  const [types, setTypes] = useState([]);       // [{value, invoices, included, reviewed}]
  const [warehouses, setWarehouses] = useState([]);
  const [gateActive, setGateActive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [dirty, setDirty] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await httpsCallable(getFunctions(), 'listCpscScopeValues')(arg());
      setTypes(res.data?.productTypes || []);
      setWarehouses(res.data?.warehouses || []);
      setGateActive(res.data?.gateActive === true);
      setDirty(false);
    } catch (e) { setError(e?.message || 'Could not load scope values.'); } finally { setLoading(false); }
  }, [arg]);
  useEffect(() => { load(); }, [load]);

  const toggle = useCallback((kind, value) => {
    const set = kind === 'type' ? setTypes : setWarehouses;
    set((prev) => prev.map((r) => (r.value === value ? { ...r, included: !r.included } : r)));
    setDirty(true); setNotice(null);
  }, []);

  const save = async () => {
    setBusy(true); setError(null); setNotice(null);
    try {
      // Reviewed vocabulary = every value visible at save time ('?' excluded — blank data isn't a code).
      // Config-only rows (count 0) are in the list via load(), so nothing seeded is silently lost.
      const vals = (rows) => rows.map((r) => s(r.value)).filter((v) => v && v !== '?');
      await httpsCallable(getFunctions(), 'manageCpscEmailRecipients')(arg({
        scopeProductTypes: vals(types.filter((r) => r.included)),
        scopeWarehouses: vals(warehouses.filter((r) => r.included)),
        scopeReviewedProductTypes: vals(types),
        scopeReviewedWarehouses: vals(warehouses),
      }));
      setNotice('Scope saved (audited). The next broker run applies it.');
      await load();
    } catch (e) { setError(e?.message || 'Could not save the scope.'); } finally { setBusy(false); }
  };

  return (
    <div className="font-plex text-carbon-ink bg-carbon-white border border-carbon-border p-3 space-y-3" aria-label="broker scope manager">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-carbon-ink flex items-center gap-1.5"><Filter size={14} /> Broker scope — production types × warehouses</h4>
        <button onClick={load} disabled={loading || busy} className="flex items-center gap-1 px-2 py-1 text-xs border border-carbon-gray-30 text-carbon-gray-70 hover:bg-carbon-gray-10 disabled:opacity-50" aria-label="reload scope values">
          <RefreshCw size={12} className={loading ? 'animate-spin' : ''} /> Reload
        </button>
      </div>
      <p className="text-xs text-carbon-gray-70">An invoice is in CPSC scope when <strong>any</strong> of its rows has an included production type <strong>and</strong> an included warehouse. Values not toggled on are excluded — every exclusion is counted in the run log, and new ERP values are flagged ⚠ until you classify them. {gateActive ? <span className="text-carbon-green-50 font-medium">Gate is ACTIVE.</span> : <span className="text-carbon-orange-70 font-medium">Gate is INERT (seed both lists and Save to activate).</span>}</p>
      {error && <div className="text-xs text-carbon-red-60 bg-carbon-white border border-carbon-border border-l-[3px] border-l-carbon-red-60 px-2 py-1 flex items-center gap-1"><AlertTriangle size={12} /> {error}</div>}
      {notice && <div className="text-xs text-carbon-gray-80 bg-carbon-green-10 border border-carbon-green-50 px-2 py-1 flex items-center gap-1"><CheckCircle2 size={12} className="text-carbon-green-50" /> {notice}</div>}
      {loading ? <div className="text-xs text-carbon-gray-50 flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading live values…</div> : (
        <>
          <ScopeGroup title="Production types (include = in CPSC scope)" rows={types} kind="type" onToggle={toggle} />
          <ScopeGroup title="US warehouses (include = in CPSC scope)" rows={warehouses} kind="warehouse" onToggle={toggle} />
          <button onClick={save} disabled={busy || !dirty} className="flex items-center gap-1 px-3 py-1.5 text-sm font-semibold bg-carbon-blue-60 text-white hover:bg-carbon-blue-70 disabled:opacity-50" aria-label="save scope">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Save scope
          </button>
        </>
      )}
    </div>
  );
}
